'use client';

import { Calendar, Users } from '@/components/ui/icons';
import { cn, getDaysLeft, formatDate } from '@/lib/utils';
import type { Project, ProjectType, ProjectStages } from '@audra-flow/types';

const typeColors: Record<ProjectType, { bg: string; text: string }> = {
  enterprise: { bg: 'bg-[#ece5ff]', text: 'text-[#512ab2]' },
  startup: { bg: 'bg-[#e7f9ef]', text: 'text-[#1f8d53]' },
  saas: { bg: 'bg-[#e7eeff]', text: 'text-[#2a4bb6]' },
  internal: { bg: 'bg-[#f1eef8]', text: 'text-[#4f4a65]' },
};

const memberColors = [
  'bg-[#25115b]',
  'bg-[#512ab2]',
  'bg-[#7c4af7]',
  'bg-[#2f80ed]',
];

const stageConfig: { key: keyof ProjectStages; label: string; color: string }[] = [
  { key: 'discovery', label: 'Disc.', color: '#7c4af7' },
  { key: 'definition', label: 'Def.', color: '#2f80ed' },
  { key: 'specification', label: 'Spec.', color: '#0ea5a0' },
  { key: 'delivery', label: 'Del.', color: '#1f8d53' },
];

interface ProjectCardProps {
  project: Project;
  onClick?: () => void;
}

export function ProjectCard({ project, onClick }: ProjectCardProps) {
  const daysLeft = getDaysLeft(project.deadline);
  const isOverdue = daysLeft < 0;
  const { bg, text } = typeColors[project.type];
  const deadlineStatus = isOverdue ? `${Math.abs(daysLeft)}d overdue` : `${daysLeft}d left`;

  return (
    <button
      onClick={onClick}
      className="wm-card relative w-full overflow-hidden rounded-[20px] p-5 text-left"
    >
      <div className="pointer-events-none absolute inset-x-0 top-0 h-16 bg-[linear-gradient(180deg,rgba(124,74,247,0.09)_0%,rgba(124,74,247,0)_100%)]" />

      <div className="relative mb-4 flex items-start justify-between">
        <div className="flex h-12 w-12 items-center justify-center rounded-xl border border-brand-200/60 bg-[linear-gradient(145deg,#050019_0%,#512ab2_58%,#7c4af7_100%)] shadow-soft-lg">
          <span className="text-[28px] leading-none">{project.emoji}</span>
        </div>
        <span className={cn('rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-[0.08em]', bg, text)}>
          {project.type}
        </span>
      </div>

      <h3 className="mb-4 font-display text-lg font-semibold text-gray-900 tracking-tight">
        {project.name}
      </h3>

      <div className="mb-5 flex flex-col gap-2.5">
        <div className="flex items-center gap-2">
          <Calendar className="w-4 h-4 text-gray-400" />
          <span className={cn('text-sm', isOverdue ? 'text-[#cf2a5b]' : 'text-gray-600')}>
            Due {formatDate(project.deadline)}
            <span className="ml-2 text-sm font-medium">
              ({deadlineStatus})
            </span>
          </span>
        </div>

        <div className="flex items-center gap-2">
          <Users className="w-4 h-4 text-gray-400" />
          <div className="flex -space-x-2">
            {project.members.slice(0, 4).map((member, idx) => (
              <div
                key={member.id}
                className={cn(
                  'flex h-8 w-8 items-center justify-center rounded-full border-2 border-white',
                  memberColors[idx % memberColors.length]
                )}
              >
                <span className="text-[10px] font-semibold text-white">{member.initials}</span>
              </div>
            ))}
            {project.members.length > 4 && (
              <div className="flex h-8 w-8 items-center justify-center rounded-full border-2 border-white bg-gray-300">
                <span className="text-xs text-gray-700">+{project.members.length - 4}</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* 4-Stage Pipeline Indicator */}
      <div className="mb-4">
        <div className="mb-2 flex items-center justify-between">
          <span className="text-sm font-medium text-gray-700">Pipeline</span>
          <span className="text-sm font-semibold text-gray-900">{project.progress}%</span>
        </div>
        <div className="flex items-center gap-1.5">
          {stageConfig.map((stage, idx) => {
            const active = project.stages[stage.key];
            return (
              <div key={stage.key} className="flex flex-1 flex-col items-center gap-1">
                <div className="flex w-full items-center">
                  <div
                    className="h-2 w-full rounded-full transition-all"
                    style={{
                      backgroundColor: active ? stage.color : '#e6e2f2',
                    }}
                  />
                  {idx < stageConfig.length - 1 && (
                    <div className="h-px w-1 shrink-0 bg-[#e6e2f2]" />
                  )}
                </div>
                <span className={cn(
                  'text-[10px] leading-none',
                  active ? 'font-medium text-gray-700' : 'text-gray-400'
                )}>
                  {stage.label}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        {project.artefacts.slice(0, 3).map((artefact) => (
          <span
            key={artefact}
            className="rounded-md border border-[#d8d1eb] bg-white/80 px-2 py-1 text-xs text-gray-700"
          >
            {artefact}
          </span>
        ))}
        {project.artefacts.length > 3 && (
          <span className="rounded-md border border-[#d8d1eb] bg-white/80 px-2 py-1 text-xs text-gray-700">
            +{project.artefacts.length - 3} more
          </span>
        )}
      </div>
    </button>
  );
}
